/**
 * Per-list overrides for outreach steps (template / ICP / cleanup guideline).
 * Keep in sync with functions/helpers/outreachTemplateMap.js.
 *
 * Persist as an array of `{ list, id|templateId, name|templateName }` — never
 * as an object keyed by list name. Import names are often `Something.csv`,
 * and Firebase RTDB rejects `.` `#` `$` `[` `]` in keys.
 */

export const LIST_MAP_TEMPLATE = {
  idKey: "templateId",
  nameKey: "templateName",
  mapKey: "templateByList",
  namesKey: "templateByListNames",
  persistIdKey: "templateId",
  persistNameKey: "templateName",
};

export const LIST_MAP_ICP = {
  idKey: "icpProfileId",
  nameKey: "icpProfileName",
  mapKey: "icpByList",
  namesKey: "icpByListNames",
  persistIdKey: "id",
  persistNameKey: "name",
};

export const LIST_MAP_GUIDELINE = {
  idKey: "guidelineId",
  nameKey: "guidelineName",
  mapKey: "guidelineByList",
  namesKey: "guidelineByListNames",
  persistIdKey: "id",
  persistNameKey: "name",
};

const asEntryList = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return null;
  const keys = Object.keys(raw);
  if (keys.length === 0) return [];
  if (!keys.every((k) => /^\d+$/.test(k))) return null;
  return keys.sort((a, b) => Number(a) - Number(b)).map((k) => raw[k]);
};

const entryListName = (item) => {
  if (!item || typeof item !== "object") return "";
  return String(item.list || item.importName || "").trim();
};

const entryOverrideId = (item) => {
  if (!item || typeof item !== "object") return "";
  for (const key of ["templateId", "icpProfileId", "guidelineId", "id"]) {
    if (typeof item[key] === "string" && item[key].trim()) return item[key].trim();
  }
  return "";
};

const entryOverrideName = (item) => {
  if (!item || typeof item !== "object") return "";
  for (const key of ["templateName", "icpProfileName", "guidelineName", "name"]) {
    if (typeof item[key] === "string" && item[key].trim()) return item[key].trim();
  }
  return "";
};

export const cleanListOverride = (raw) => {
  const out = {};
  const entries = asEntryList(raw);
  if (entries) {
    for (const item of entries) {
      const list = entryListName(item);
      const id = entryOverrideId(item);
      if (!list || !id) continue;
      out[list] = id;
    }
    return out;
  }
  if (!raw || typeof raw !== "object") return {};
  for (const [rawList, rawId] of Object.entries(raw)) {
    if (rawId && typeof rawId === "object") continue;
    const list = String(rawList || "").trim();
    const id = typeof rawId === "string" ? rawId.trim() : "";
    if (!list || !id) continue;
    out[list] = id;
  }
  return out;
};

export const cleanTemplateByList = cleanListOverride;

export const readListOverrideNames = (step, spec) => {
  const out = {};
  const entries = asEntryList(step && step[spec.mapKey]);
  if (entries) {
    for (const item of entries) {
      const list = entryListName(item);
      const name = entryOverrideName(item);
      if (list && name) out[list] = name;
    }
  }
  const names = step && step[spec.namesKey];
  if (names && typeof names === "object" && !Array.isArray(names) && !asEntryList(names)) {
    for (const [rawList, rawName] of Object.entries(names)) {
      if (typeof rawName !== "string") continue;
      const list = rawList.trim();
      const name = rawName.trim();
      if (list && name) out[list] = name;
    }
  }
  return out;
};

export const readTemplateByListNames = (step) => readListOverrideNames(step, LIST_MAP_TEMPLATE);

const fallbackId = (step, spec) => {
  const raw = step && step[spec.idKey];
  return typeof raw === "string" ? raw.trim() : "";
};

const fallbackName = (step, spec) => {
  const raw = step && step[spec.nameKey];
  return typeof raw === "string" ? raw : "";
};

export const pruneListOverride = (step, listNames, spec) => {
  const allowed = new Set(Array.isArray(listNames) ? listNames.filter(Boolean) : []);
  const map = cleanListOverride(step && step[spec.mapKey]);
  const names = readListOverrideNames(step, spec);
  const nextMap = {};
  const nextNames = {};
  for (const [list, id] of Object.entries(map)) {
    if (!allowed.has(list)) continue;
    nextMap[list] = id;
    if (names[list]) nextNames[list] = names[list];
  }
  return {
    ...(step || {}),
    [spec.mapKey]: nextMap,
    [spec.namesKey]: nextNames,
  };
};

export const pruneTemplateByList = (step, listNames) => pruneListOverride(step, listNames, LIST_MAP_TEMPLATE);

export const persistListOverride = (step, spec) => {
  if (!step || typeof step !== "object") return step;
  const map = cleanListOverride(step[spec.mapKey]);
  const names = readListOverrideNames(step, spec);
  const next = { ...step };
  delete next[spec.namesKey];
  const entries = Object.entries(map).map(([list, id]) => {
    const row = { list, [spec.persistIdKey]: id };
    if (names[list]) row[spec.persistNameKey] = names[list];
    return row;
  });
  if (entries.length === 0) {
    delete next[spec.mapKey];
  } else {
    next[spec.mapKey] = entries;
  }
  return next;
};

export const persistTemplateByList = (step) => persistListOverride(step, LIST_MAP_TEMPLATE);

export const persistStepListMaps = (step, listNames) => {
  if (!step || typeof step !== "object") return step;
  const spec = step.type === "send_email"
    ? LIST_MAP_TEMPLATE
    : step.type === "potential_analyzer"
      ? LIST_MAP_ICP
      : step.type === "clean"
        ? LIST_MAP_GUIDELINE
        : null;
  if (!spec) return step;
  const next = Array.isArray(listNames) ? pruneListOverride(step, listNames, spec) : step;
  return persistListOverride(next, spec);
};

export const resolveListOverride = (step, importName, spec) => {
  const id = fallbackId(step, spec);
  const name = fallbackName(step, spec);
  const map = cleanListOverride(step && step[spec.mapKey]);
  const key = typeof importName === "string" ? importName.trim() : "";
  const overrideId = key && map[key] ? map[key] : "";
  if (!overrideId || overrideId === id) {
    return { id, name, source: "default" };
  }
  const names = readListOverrideNames(step, spec);
  return {
    id: overrideId,
    name: names[key] || overrideId,
    source: "list",
    listName: key,
  };
};

export const resolveStepTemplate = (step, importName) => {
  const resolved = resolveListOverride(step, importName, LIST_MAP_TEMPLATE);
  return {
    templateId: resolved.id,
    templateName: resolved.name,
    source: resolved.source,
    ...(resolved.listName ? { listName: resolved.listName } : {}),
  };
};

export const resolveStepIcp = (step, importName) => resolveListOverride(step, importName, LIST_MAP_ICP);

export const resolveStepGuideline = (step, importName) => resolveListOverride(step, importName, LIST_MAP_GUIDELINE);

export const collectOverrideIds = (step, spec) => {
  const ids = [];
  const fallback = fallbackId(step, spec);
  if (fallback) ids.push(fallback);
  Object.values(cleanListOverride(step && step[spec.mapKey])).forEach((id) => ids.push(id));
  return ids;
};

export const collectStepTemplateIds = (step) => collectOverrideIds(step, LIST_MAP_TEMPLATE);
